import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../api/client';

const initialState = {
  items: [],
  meta: {
    page: 1,
    pageSize: 10,
    totalItems: 0
  },
  filters: {
    name: '',
    status: 'ALL',
    startDate: '',
    endDate: ''
  },
  sortBy: 'startDate',
  sortDirection: 'desc',
  page: 0,
  pageSize: 10,
  status: 'idle',
  error: null,
  createStatus: 'idle',
  createError: null
};

const toLocalDateOnly = (dateObj) => {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const normalizeDateOnly = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    // If it's already a plain date string, use it
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return toLocalDateOnly(parsed);
    }
    return value.slice(0, 10);
  }
  try {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return toLocalDateOnly(d);
    }
  } catch (e) {
    return '';
  }
  return '';
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  return query.toString();
};

export const fetchCampaigns = createAsyncThunk(
  'campaigns/fetchCampaigns',
  async (params = {}, { getState, rejectWithValue }) => {
    const queryString = buildQuery(params);
    let data;
    try {
      data = await apiFetch(`/api/campaigns${queryString ? `?${queryString}` : ''}`);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch campaigns');
    }
    const rawData = data.data;
    const rawItems = Array.isArray(rawData) ? rawData : rawData?.items || [];
    const items = rawItems.map((item) => ({
      ...item,
      id: item.id,
      name: item.name,
      status: item.status || '',
      startDate: normalizeDateOnly(item.startDate || item.start_date),
      endDate: normalizeDateOnly(item.endDate || item.end_date),
      priority: item.priority || '',
      description: item.description || '',
      totalImpression: item.totalImpression ?? item.total_impression,
      achievedTotalImpression: item.achievedTotalImpression ?? item.achieved_total_impression,
      achievedDailyImpression: item.achievedDailyImpression ?? item.achieved_daily_impression
    }));
    const meta =
      data.meta ||
      rawData?.meta || {
        page: 1,
        pageSize: params.pageSize || 10,
        totalItems: items.length
      };
    return {
      items,
      meta
    };
  }
);

export const createCampaign = createAsyncThunk(
  'campaigns/createCampaign',
  async (body, { getState, dispatch, rejectWithValue }) => {
    const hasId = Boolean(body?.id);
    const url = hasId ? `/api/campaigns/${body.id}` : `/api/campaigns`;
    const method = hasId ? 'PUT' : 'POST';
    let data;
    try {
      data = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create campaign');
    }
    const newId = data.data?.id;

    // Refresh list after create/update
    dispatch(
      fetchCampaigns({
        page: 1,
        pageSize: 10
      })
    );

    return { ...body, id: newId || body.id };
  }
);

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
      state.page = 0;
    },
    setSort: (state, action) => {
      state.sortBy = action.payload.sortBy;
      state.sortDirection = action.payload.sortDirection;
      state.page = 0;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 0;
    },
    resetCampaignsState: () => ({
      ...initialState,
      filters: { ...initialState.filters }
    })
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.error = null;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch campaigns';
      })
      .addCase(createCampaign.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.createStatus = 'succeeded';
        // List is refreshed via fetchCampaigns after create/update; no local prepend/replace needed.
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.createError = action.payload || 'Failed to create campaign';
      });
  }
});

export const { setFilters, setSort, setPage, setPageSize, resetCampaignsState } =
  campaignsSlice.actions;

export default campaignsSlice.reducer;
